import { useCallback, useRef, useState } from 'react';
import ExportProgressModal, { type ClientExportPhase } from '../components/ExportProgressModal';
import { triggerBlobDownload } from '../utils/triggerBlobDownload';

export type ClientExportFile = { blob: Blob; fileName: string };

export type ClientExportWorkResult = {
  files: ClientExportFile | ClientExportFile[];
  summaryLines?: string[];
};

export interface UseClientExportFlowOptions {
  /** Override stacking (default `z-[200]` on modal); modal is portaled to `document.body`. */
  zClassName?: string;
}

export function useClientExportFlow(options?: UseClientExportFlowOptions) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<ClientExportPhase>('starting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [summaryLines, setSummaryLines] = useState<string[] | undefined>();
  const [downloadButtonLabel, setDownloadButtonLabel] = useState('Download CSV');
  const pendingFiles = useRef<ClientExportFile[]>([]);

  const close = useCallback(() => {
    setOpen(false);
    setPhase('starting');
    setErrorMessage(null);
    setSummaryLines(undefined);
    pendingFiles.current = [];
  }, []);

  const handleDownload = useCallback(async () => {
    const files = pendingFiles.current;
    for (let i = 0; i < files.length; i++) {
      const f = files[i]!;
      triggerBlobDownload(f.blob, f.fileName);
      if (i < files.length - 1) {
        await new Promise((r) => setTimeout(r, 450));
      }
    }
    close();
  }, [close]);

  const run = useCallback(
    async (opts: { downloadButtonLabel?: string; work: () => Promise<ClientExportWorkResult> }) => {
      setOpen(true);
      setPhase('starting');
      setErrorMessage(null);
      setSummaryLines(undefined);
      pendingFiles.current = [];
      setDownloadButtonLabel(opts.downloadButtonLabel ?? 'Download CSV');
      await Promise.resolve();
      try {
        setPhase('processing');
        const result = await opts.work();
        const list = Array.isArray(result.files) ? result.files : [result.files];
        pendingFiles.current = list;
        setSummaryLines(result.summaryLines);
        setPhase('completed');
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : 'Export failed');
        setPhase('failed');
      }
    },
    []
  );

  const modal = (
    <ExportProgressModal
      variant="client"
      open={open}
      zClassName={options?.zClassName}
      phase={phase}
      errorMessage={errorMessage}
      clientSummaryLines={summaryLines}
      downloadButtonLabel={downloadButtonLabel}
      onDownload={phase === 'completed' ? handleDownload : undefined}
      onClose={close}
    />
  );

  return { run, modal, close };
}
